// Ceiling depth checker for designers: how deep the false ceiling must be
// for a Daikin VRV ducted unit or cassette, and the clear room height that
// leaves.
//
//   ceiling depth = the depth to plan for (src/data/indoorUnits.js: Daikin's
//                   ceiling figure plus 1–2 inches to service the unit)
//                   + drain fall, where the drain runs by gravity at the
//                   unit's level (10 mm a metre: Daikin's 1:100)
//                   + ducts or beams crossing below the unit
//   clear height  = floor-to-slab − ceiling depth
// Compared with NBC 2016: 2.75 m for habitable rooms, 2.4 m to the false
// ceiling or duct in air-conditioned rooms. The verdict uses the deeper end.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DRAIN_FALL_PER_M, NBC_HABITABLE_MM, NBC_AC_ROOM_MM } from '../data/designerLibrary';
import { VRV_INDOOR_UNITS, planMm, tonRange, unitHeightText } from '../data/indoorUnits';

export const CHECKER_UNITS = VRV_INDOOR_UNITS.filter((u) => u.plan);

const FT = 304.8;
const inputCls = 'h-11 w-full rounded-lg border border-gray-300 px-3 text-base text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const mm = (n) => Math.round(n).toLocaleString('en-IN');
const ftIn = (n) => {
  const inches = Math.round(n / 25.4);
  return `${Math.floor(inches / 12)} ft ${inches % 12} in`;
};
const inch = (n) => Math.round(n / 25.4);

export default function CeilingChecker({ unitId: controlled, onUnitChange }) {
  const [own, setOwn] = useState('slim-duct');
  const unitId = controlled || own;
  const setUnitId = (id) => { setOwn(id); if (onUnitChange) onUnitChange(id); };
  const [unitSys, setUnitSys] = useState('ft');
  const [height, setHeight] = useState('10');
  const [drain, setDrain] = useState('0');
  const [ducts, setDucts] = useState('0');

  const unit = CHECKER_UNITS.find((u) => u.id === unitId) || CHECKER_UNITS[0];
  const r = useMemo(() => {
    const h = parseFloat(height);
    const slab = unitSys === 'ft' ? h * FT : h;
    if (!(slab > 1500 && slab < 10000)) return null;
    const [lo, hi] = planMm(unit);
    const fall = Math.max(0, parseFloat(drain) || 0) * DRAIN_FALL_PER_M;
    const below = Math.max(0, parseFloat(ducts) || 0);
    const voidLo = lo + fall + below;
    const voidHi = hi + fall + below;
    return { slab, lo, hi, fall, below, voidLo, voidHi, clearLo: slab - voidHi, clearHi: slab - voidLo };
  }, [height, unitSys, drain, ducts, unit]);

  const verdict = !r ? null
    : r.clearLo >= NBC_HABITABLE_MM ? { tone: 'green', text: 'Meets 2.75 m: fine for any habitable room under NBC 2016.' }
      : r.clearLo >= NBC_AC_ROOM_MM ? { tone: 'blue', text: 'Meets 2.4 m: allowed for air-conditioned rooms under NBC 2016, measured to the false ceiling. Check your local bye-laws.' }
        : { tone: 'amber', text: 'Below 2.4 m. Drop the ceiling only over a passage, wardrobe or bulkhead and throw air in from the side, use the 1-way cassette (8–9 in), or a wall-mounted or ceiling-suspended unit.' };
  const toneCls = { green: 'bg-green-50 text-green-900', blue: 'bg-blue-50 text-blue-900', amber: 'bg-amber-50 text-amber-900' };

  return (
    <div id="ceiling-tool" className="scroll-mt-36 rounded-2xl border-2 border-blue-700 bg-white p-4 sm:p-5">
      <p className="text-[11px] font-bold tracking-[0.15em] text-blue-700">FREE TOOL</p>
      <h3 className="mt-0.5 text-lg font-extrabold text-gray-900">Ceiling depth checker</h3>
      <p className="mt-1 text-xs leading-relaxed text-gray-600">How deep the false ceiling must be for a Daikin VRV ducted unit or cassette, with room to service it, and the room height it leaves.</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="cc-unit" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Indoor unit</label>
          <select id="cc-unit" value={unit.id} onChange={(e) => setUnitId(e.target.value)} className={inputCls}>
            {CHECKER_UNITS.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.series}), {tonRange(u)}</option>)}
          </select>
          <p className="mt-1 text-[11px] text-gray-500">Unit height {unitHeightText(unit)}. Plan for {unit.plan[0]}–{unit.plan[1]} in{unit.ducts ? ', plus ducts' : ''}.</p>
        </div>
        <div>
          <label htmlFor="cc-h" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Floor to slab</label>
          <div className="flex gap-2">
            <input id="cc-h" inputMode="decimal" value={height} onChange={(e) => setHeight(e.target.value.replace(/[^\d.]/g, '').slice(0, 6))} className={inputCls} />
            <div className="flex shrink-0 overflow-hidden rounded-lg border border-gray-300" role="group" aria-label="Units">
              {['ft', 'mm'].map((s) => (
                <button key={s} type="button" aria-pressed={unitSys === s}
                  onClick={() => {
                    const h = parseFloat(height);
                    if (s !== unitSys && h > 0) setHeight(s === 'mm' ? String(Math.round(h * FT)) : (h / FT).toFixed(1));
                    setUnitSys(s);
                  }}
                  className={`h-11 w-12 text-sm ${unitSys === s ? 'bg-blue-700 font-bold text-white' : 'bg-white text-gray-700'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <label htmlFor="cc-d" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Gravity drain at the unit&apos;s level (m)</label>
          <input id="cc-d" inputMode="decimal" value={drain} onChange={(e) => setDrain(e.target.value.replace(/[^\d.]/g, '').slice(0, 4))} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cc-x" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Ducts or beams crossing below the unit (mm)</label>
          <input id="cc-x" inputMode="numeric" value={ducts} onChange={(e) => setDucts(e.target.value.replace(/\D/g, '').slice(0, 4))} className={inputCls} />
        </div>
      </div>

      {r ? (
        <div className="mt-4 rounded-xl bg-gray-50 p-4" aria-live="polite">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-600">Ceiling depth</p>
              <p className="text-xl font-extrabold text-gray-900 sm:text-2xl">{mm(r.voidLo)}–{mm(r.voidHi)} mm</p>
              <p className="text-[11px] text-gray-500">{inch(r.voidLo)}–{inch(r.voidHi)} in</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Clear room height</p>
              <p className="text-xl font-extrabold text-gray-900 sm:text-2xl">{mm(r.clearLo)}–{mm(r.clearHi)} mm</p>
              <p className="text-[11px] text-gray-500">{ftIn(r.clearLo)} to {ftIn(r.clearHi)}</p>
            </div>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-gray-600">
            The unit with room to service it: {mm(r.lo)}–{mm(r.hi)} mm
            {r.fall ? ` + drain fall ${mm(r.fall)} mm` : ''}
            {r.below ? ` + ${mm(r.below)} mm crossing below` : ''}.
            {' '}Most ceiling units have a drain pump, so the drain runs high and adds little.
          </p>
          <p className={`mt-3 rounded-lg px-3 py-2 text-[13px] font-medium leading-snug ${toneCls[verdict.tone]}`}>{verdict.text}</p>
        </div>
      ) : (
        <p className="mt-4 rounded-xl bg-gray-50 p-4 text-sm text-gray-600">Enter the floor-to-slab height.</p>
      )}
      <p className="mt-3 text-[11px] leading-relaxed text-gray-500">
        Unit heights from Daikin&apos;s VRV Alpha catalogue, plus 1–2 inches to service the unit. Drain fall at Daikin&apos;s minimum slope of 1:100. Room heights from the National Building Code of India 2016.
        For the exact unit on your project, <Link to="/hvac-consultant?service=call&from=ceiling-tool" className="font-semibold text-blue-700">book a ₹99 video call</Link>.
      </p>
    </div>
  );
}
